import { computed, type Ref } from "vue"
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from "../stores/productStore"
import { 
  MMS_BASE_UNIT_SKU, 
  MMS_ADDON_SKU, 
  MMS_SHARED_BOLT_KIT_SKU, 
  MMS_SOLO_BOLT_KIT_SKU, 
  MMS_4_PACK_WHEEL_SKU, 
  MMS_2_PACK_WHEEL_SKU,
  MMS_CONFIG_SKU,
  MMS_GROUPED_SKU, 
  MMS_CORNER_2_PACK_SKU, 
  MMS_CORNER_3_PACK_SKU, 
  MMS_CORNER_4_PACK_SKU, 
  MMS_GROUP_CHILD_SKUS,
  MMSUnit} from "../pages/product/rackbuilder/utils/mms-utils/mms-constants"
import {
  MS_BASE_UNIT_SKU,
  MS_ADDON_SKU,
  MS_4_PACK_BOLT_KIT_SKU,
  MS_16_PACK_BOLT_KIT_SKU,
  MS_4_PACK_WHEEL_SKU,
  MS_2_PACK_WHEEL_SKU,
  MS_CONFIG_SKU,
  MS_GROUPED_SKU,
  MS_CORNER_2_PACK_SKU,
  MS_CORNER_3_PACK_SKU,
  MS_GROUP_CHILD_SKUS,
  MSUnit,
  withMsSitePrefix} from "../pages/product/rackbuilder/utils/ms-utils/ms-constants"
import type { BuyRequest } from "../services/RogueTypes"

export function useMassStorage(buyRequestRef: Ref<BuyRequest | undefined | null>) {
  const productStore = useProductStore()
  const rogue = useRogue()

  const withMsPrefix = (baseSku: string) => withMsSitePrefix(baseSku, rogue.site.code)

  const monsterMassStorageSKUs = [MMS_GROUPED_SKU, MMS_BASE_UNIT_SKU, MMS_CONFIG_SKU, MMS_ADDON_SKU, MMS_SOLO_BOLT_KIT_SKU, MMS_SHARED_BOLT_KIT_SKU]
  // Bundle-child skus only get the EU- prefix — MS_GROUPED_SKU is the parent group sku, identical on every site.
  const massStorageSKUs = computed(() => [
    MS_GROUPED_SKU,
    withMsPrefix(MS_BASE_UNIT_SKU),
    withMsPrefix(MS_CONFIG_SKU),
    withMsPrefix(MS_ADDON_SKU),
    withMsPrefix(MS_4_PACK_BOLT_KIT_SKU),
    withMsPrefix(MS_16_PACK_BOLT_KIT_SKU),
  ])

  const productId = computed(() => buyRequestRef.value?.productId)
  const product = computed(() => {
    const id = productId.value

    return id ? productStore.products[id] : undefined
  })
  const sku = computed(() => (product.value?.sku || '').toUpperCase())

  const isMonsterMassStorage = computed(() => monsterMassStorageSKUs.includes(sku.value) && !!rogue.featureFlags?.isOn('enable-monster-mass-storage'))
  const isMassStorage = computed(() => massStorageSKUs.value.includes(sku.value) && !!rogue.featureFlags?.isOn('enable-mass-storage'))
  const is3DStorageProduct = computed(() => isMonsterMassStorage.value || isMassStorage.value)

  // Only the base-unit bundle is rendered by CustomOrderItem, which owns the cascade remove/move.
  // Standalone accessories (bolt kits) are plain OrderItems and must act via the store directly.
  const isStorageMainBundle = computed(() =>
    (isMonsterMassStorage.value && sku.value === MMS_BASE_UNIT_SKU) ||
    (isMassStorage.value && sku.value === withMsPrefix(MS_BASE_UNIT_SKU))
  )

  const STORAGE_SKUS = computed(() => ({
    GROUP_SKU     : isMonsterMassStorage.value ? MMS_GROUPED_SKU : withMsPrefix(MS_GROUPED_SKU),
    BASE_UNIT_SKU : isMonsterMassStorage.value ? MMS_BASE_UNIT_SKU : withMsPrefix(MS_BASE_UNIT_SKU),
    ADDON_SKU     : isMonsterMassStorage.value ? MMS_ADDON_SKU : withMsPrefix(MS_ADDON_SKU),

    GROUP_CHILD_SKUS : isMonsterMassStorage.value ? MMS_GROUP_CHILD_SKUS : MS_GROUP_CHILD_SKUS.map(withMsPrefix),
    UNIT_SKUS        : isMonsterMassStorage.value ? MMSUnit : MSUnit.map(withMsPrefix),

    // Shared/solo is an MMS-only concept — MS has no such split.
    SHARED_BOLT_KIT_SKU : MMS_SHARED_BOLT_KIT_SKU,
    SOLO_BOLT_KIT_SKU   : MMS_SOLO_BOLT_KIT_SKU,

    // 4-pack / 16-pack bolt kits are MS-only.
    FOUR_PACK_BOLT_KIT_SKU    : withMsPrefix(MS_4_PACK_BOLT_KIT_SKU),
    SIXTEEN_PACK_BOLT_KIT_SKU : withMsPrefix(MS_16_PACK_BOLT_KIT_SKU),

    // All bolt-kit SKUs for the current product — MMS shared/solo, or MS 4-pack/16-pack.
    // Use this for generic "is this a bolt kit" checks and preloading.
    BOLT_KIT_SKUS: isMonsterMassStorage.value
      ? [MMS_SHARED_BOLT_KIT_SKU, MMS_SOLO_BOLT_KIT_SKU]
      : [MS_4_PACK_BOLT_KIT_SKU, MS_16_PACK_BOLT_KIT_SKU].map(withMsPrefix),

    FOUR_PACK_WHEEL_SKU : isMonsterMassStorage.value ? MMS_4_PACK_WHEEL_SKU : withMsPrefix(MS_4_PACK_WHEEL_SKU),
    TWO_PACK_WHEEL_SKU  : isMonsterMassStorage.value ? MMS_2_PACK_WHEEL_SKU : withMsPrefix(MS_2_PACK_WHEEL_SKU),

    CORNER_2_PACK_SKU : isMonsterMassStorage.value ? MMS_CORNER_2_PACK_SKU : withMsPrefix(MS_CORNER_2_PACK_SKU),
    CORNER_3_PACK_SKU : isMonsterMassStorage.value ? MMS_CORNER_3_PACK_SKU : withMsPrefix(MS_CORNER_3_PACK_SKU),
    CORNER_4_PACK_SKU : MMS_CORNER_4_PACK_SKU,
  }))

  return {
    isMonsterMassStorage,
    isMassStorage,
    is3DStorageProduct,
    isStorageMainBundle,
    STORAGE_SKUS,
  }
}